/**
 * Layout for /support-console/* — same chrome as PlatformLayout (top bar with
 * brand + staff badge + locale switcher + sign-out, a left sidebar, and a
 * standard page header), scoped to the support-agent surface.
 */
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { useLogout } from "@/lib/auth";
import { useAgentMe } from "@/lib/support";

import { LocaleSwitcher } from "./LocaleSwitcher";

const NAV: { to: string; labelKey: string; end?: boolean }[] = [
  { to: "/support-console", labelKey: "support.console.nav.queue", end: true },
];

function NavLinks() {
  const { t } = useTranslation();
  return (
    <nav className="space-y-0.5">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            "block rounded-md px-3 py-1.5 text-sm transition " +
            (isActive
              ? "bg-nb-mint/15 text-nb-mint"
              : "text-nb-text-2 hover:bg-nb-surface/40 hover:text-nb-text")
          }
        >
          {t(item.labelKey)}
        </NavLink>
      ))}
    </nav>
  );
}

export function SupportConsoleLayout() {
  const me = useAgentMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-40 border-b border-nb-border bg-nb-bg/80 backdrop-blur">
        <div className="mx-auto flex max-w-screen-2xl 2xl:max-w-[105rem] items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Link to="/support-console" className="font-semibold tracking-tight text-nb-text">
              Nex<span className="text-nb-mint">.</span>Basira
              <span className="text-nb-mint">/</span>
            </Link>
            <span className="rounded-md bg-nb-mint/20 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-nb-mint">
              {t("support.console.badge")}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-nb-text">
            {me.data?.email && (
              <span className="hidden text-nb-text-2 sm:inline">{me.data.email}</span>
            )}
            <LocaleSwitcher />
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-nb-border px-3 py-1.5 text-sm font-medium text-nb-text-2 transition hover:border-nb-danger/60 hover:bg-nb-danger/10 hover:text-nb-danger"
              onClick={() => {
                logout.mutate(undefined, {
                  onSettled: () => navigate("/login", { replace: true }),
                });
              }}
              title={t("auth.signOut")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t("auth.signOut")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-screen-2xl 2xl:max-w-[105rem] px-6 py-8 lg:grid lg:grid-cols-[12rem_1fr] lg:gap-10">
        <aside className="hidden self-start lg:sticky lg:top-24 lg:block">
          <NavLinks />
        </aside>
        <details className="mb-4 lg:hidden">
          <summary className="flex cursor-pointer items-center justify-between rounded-md border border-nb-border bg-nb-surface/30 px-4 py-2.5 text-sm text-nb-text">
            <span className="font-medium">{t("sidebar.menu")}</span>
          </summary>
          <div className="mt-3 rounded-md border border-nb-border bg-nb-surface/20 p-3">
            <NavLinks />
          </div>
        </details>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Standard page header for the support console — mirrors PlatformPageHeader. */
export function SupportConsolePageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="mb-6 flex items-start justify-between gap-4">
      <div className="min-w-0 space-y-2">
        <div className="eyebrow">{t("support.console.eyebrow")}</div>
        <h1 className="page-title">{title}</h1>
        {description && (
          <p className="section-lead text-sm sm:text-base">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}
